import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Smartphone, TrendingUp,
  EyeOff, Power, MessageSquare, MapPinned, Zap
} from "lucide-react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { CmsImageSlideshow as CmsImage } from "../lib/images";
import { CmsText } from "../lib/texts";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { waProps, waLabel } from "../lib/whatsapp";

/**
 * Dedicated editorial page for the e-Tracker service.
 * Client's verbatim brochure copy on top of a modern cinematic layout.
 * Four client-supplied photos (delivery vans, family ATV, bus fleet,
 * heavy machinery) illustrate the range of vehicles the system covers.
 */
export default function ETracker() {
  const { pick } = useI18n();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data?.data || null)).catch(() => {});
    api.post("/track/visit", { path: "/servicios/e-tracker" }).catch(() => {});
  }, []);

  // Verbatim copy — kept in ES and faithfully translated to EN / PT
  const P1_ES = "Con e-TRACKER usted podrá conocer la ubicación exacta del vehículo en cualquier lugar, o simplemente desde un smartphone, las veces que requiera y en tiempo real.";
  const P1_EN = "With e-TRACKER you can know the exact location of your vehicle anywhere — or simply from your smartphone — as many times as you need, in real time.";
  const P1_PT = "Com o e-TRACKER você pode saber a localização exata do veículo em qualquer lugar — ou simplesmente pelo smartphone — quantas vezes precisar, em tempo real.";

  const P2_ES = "e-TRACKER es tan pequeño que se puede ocultar fácilmente en la unidad, sin necesidad de que los operadores sepan su ubicación, para tenerlos totalmente vigilados y supervisados. Con ello, evitas que utilicen tus unidades para hacer un negocio propio.";
  const P2_EN = "e-TRACKER is small enough to be easily hidden in the unit — without operators knowing its location — keeping them fully monitored and supervised. This prevents your units from being used for private business.";
  const P2_PT = "O e-TRACKER é tão pequeno que pode ser facilmente ocultado na unidade — sem que os operadores saibam sua localização — mantendo-os totalmente vigiados e supervisionados. Assim, você evita que usem suas unidades para negócios próprios.";

  const P3_ES = "E-tracker no es un gasto, es una inversión. Con e-Tracker elevarás significativamente tus ventas — obtienes ventaja competitiva ofreciéndoles a tus clientes (cuando lo requieras) la oportunidad de ver el traslado de su mercancía en tiempo real y de forma ilimitada.";
  const P3_EN = "E-tracker is not an expense — it's an investment. With e-Tracker you'll significantly boost sales by gaining a competitive edge: offer clients (when you choose) the option to watch their shipment move in real time, unlimited.";
  const P3_PT = "E-tracker não é um gasto, é um investimento. Com o e-Tracker suas vendas crescem significativamente — você obtém vantagem competitiva oferecendo aos clientes (quando desejar) a possibilidade de acompanhar sua mercadoria em tempo real, sem limites.";

  const P4_ES = "e-Tracker es más que una localización: es una herramienta logística de operación para transportistas y flotilleros. Permite comunicación bi-direccional con tus operadores, dándote control total de las operaciones de tus unidades en tiempo real.";
  const P4_EN = "e-Tracker is more than location — it's an operational logistics tool for carriers and fleet operators. Two-way communication with your drivers gives you total control of your units in real time.";
  const P4_PT = "O e-Tracker é mais do que localização: é uma ferramenta logística de operação para transportadores e frotistas. Permite comunicação bidirecional com seus operadores, dando controle total das operações em tempo real.";

  const P5_ES = "Con e-Tracker podrás tener las rutas y sus tiempos, ya sea de forma gráfica o en base de datos, e inclusive habilitar o deshabilitar el encendido cuando lo requieras. Rastrea silenciosamente la ubicación exacta con un tiempo de respuesta de ~5 segundos — y en emergencias, cada 2 segundos. Además de geocercas perimetrales, puedes definir rutas de punto a punto que los operadores deben seguir.";
  const P5_EN = "With e-Tracker you get routes and their timings — visually or in a database — and can enable or disable ignition whenever you need. It silently tracks the exact location with ~5-second response time — and, in emergencies, every 2 seconds. Beyond perimeter geofences, you can define point-to-point routes that operators must follow.";
  const P5_PT = "Com o e-Tracker você tem rotas e seus tempos — de forma gráfica ou em base de dados — e pode habilitar ou desabilitar a ignição quando precisar. Rastreia silenciosamente a localização exata com tempo de resposta de ~5 segundos — em emergências, a cada 2 segundos. Além de cercas perimetrais, você pode definir rotas ponto-a-ponto que os operadores devem seguir.";

  const FEATURES = [
    { Icon: Smartphone,  t: pick("Ubicación desde smartphone", "Smartphone location", "Localização pelo smartphone"),
      d: pick("Consulta la posición exacta desde cualquier celular o computadora, en tiempo real.", "Check exact position from any phone or computer, in real time.", "Consulte a posição exata de qualquer celular ou computador, em tempo real.") },
    { Icon: EyeOff,      t: pick("Instalación oculta", "Hidden install", "Instalação oculta"),
      d: pick("Tan pequeño que se oculta sin que el operador lo sepa — supervisión discreta.", "Small enough to hide without the operator knowing — discreet supervision.", "Tão pequeno que se oculta sem que o operador saiba — supervisão discreta.") },
    { Icon: Power,       t: pick("Encendido remoto", "Remote ignition", "Ignição remota"),
      d: pick("Habilita o deshabilita el encendido a distancia cuando la operación lo requiera.", "Enable or disable ignition remotely whenever operations require it.", "Habilite ou desabilite a ignição remotamente quando a operação exigir.") },
    { Icon: MessageSquare, t: pick("Comunicación bi-direccional", "Two-way communication", "Comunicação bidirecional"),
      d: pick("Mensajes de texto y voz con el operador — control total de la operación.", "Text and voice messages with the driver — full operational control.", "Mensagens de texto e voz com o operador — controle total da operação.") },
    { Icon: MapPinned,   t: pick("Rutas punto a punto", "Point-to-point routes", "Rotas ponto a ponto"),
      d: pick("Además de geocercas perimetrales, define rutas obligatorias entre dos puntos.", "Beyond perimeter geofences, define required routes between two points.", "Além das cercas perimetrais, defina rotas obrigatórias entre dois pontos.") },
    { Icon: TrendingUp,  t: pick("Ventaja competitiva", "Competitive edge", "Vantagem competitiva"),
      d: pick("Ofrece a tus clientes visibilidad en tiempo real de sus envíos y eleva tus ventas.", "Offer clients real-time visibility of their shipments and boost your sales.", "Ofereça aos clientes visibilidade em tempo real dos envios e aumente suas vendas.") },
  ];

  return (
    <div className="min-h-screen text-white" data-testid="e-tracker-page">
      <NavBar />

      {/* ═══════ HERO ═══════ */}
      <section className="relative overflow-hidden h-[62vh] min-h-[440px] max-h-[720px] bg-[#06080D]">
        <CmsImage
          slotKey="etracker.hero"
          defaultSrc="/e-tracker/vans-fleet.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-75"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06080D] via-[#06080D]/70 to-[#06080D]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06080D] via-transparent to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-[0.08]" />

        <div className="relative h-full max-w-7xl mx-auto px-5 sm:px-6 md:px-10 flex items-end pb-10 md:pb-14 pt-32 sm:pt-36 md:pt-40">
          <div className="max-w-2xl">
            <Link to="/servicios" className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-300 transition mb-4" data-testid="back-to-services">
              <ArrowLeft className="w-4 h-4" />
              <span className="label-mono">{pick("SERVICIOS / ALL", "SERVICES / ALL", "SERVIÇOS / ALL")}</span>
            </Link>
          </div>
        </div>

        {/* Centered page title over the banner */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none px-4"
          data-testid="etracker-banner-title"
        >
          <span
            className="font-display chrome-text text-4xl sm:text-6xl md:text-7xl lg:text-[7rem] tracking-[0.08em] leading-none text-center whitespace-nowrap"
            style={{ filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.85))' }}
          >
            E-TRACKER
          </span>
        </motion.h1>
        <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-b from-transparent to-[#06080D] pointer-events-none" />
      </section>

      {/* ═══════ BLOCK #1 — Location from smartphone ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="absolute top-1/3 -left-40 w-[560px] h-[560px] rounded-full bg-cyan-500/8 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage slotKey="etracker.atv" defaultSrc="/e-tracker/atv-family.jpg" alt="" className="w-full h-full object-cover aspect-square" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#06080D]/35 via-transparent to-cyan-500/10 mix-blend-multiply" />
                <div className="absolute bottom-4 left-4 chrome-border rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(6,8,13,0.85)', backdropFilter: 'blur(8px)' }}>
                  <Smartphone className="w-3.5 h-3.5 text-cyan-300" />
                  <span className="label-mono !text-cyan-300 text-[10px]">{pick("UBICACIÓN EN TIEMPO REAL", "REAL-TIME LOCATION", "LOCALIZAÇÃO EM TEMPO REAL")}</span>
                </div>
              </div>
              <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-cyan-400/40 pointer-events-none" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-7 lg:-mt-24"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">{pick(P1_ES, P1_EN, P1_PT)}</p>
              <p className="mt-4 text-white/85 text-base sm:text-lg leading-relaxed">{pick(P2_ES, P2_EN, P2_PT)}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ BLOCK #2 — Investment, not expense ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="absolute bottom-1/3 -right-40 w-[560px] h-[560px] rounded-full bg-emerald-400/6 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-7 order-2 lg:order-1"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">{pick(P3_ES, P3_EN, P3_PT)}</p>
              <p className="mt-4 text-white/85 text-base sm:text-lg leading-relaxed">{pick(P4_ES, P4_EN, P4_PT)}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-5 order-1 lg:order-2 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage slotKey="etracker.buses" defaultSrc="/e-tracker/buses-fleet.jpg" alt="" className="w-full h-full object-cover aspect-[4/3]" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-[#06080D]/45 via-transparent to-emerald-500/10 mix-blend-multiply" />
                <div className="absolute bottom-4 right-4 chrome-border rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(6,8,13,0.85)', backdropFilter: 'blur(8px)' }}>
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-300" />
                  <span className="label-mono !text-emerald-300 text-[10px]">{pick("VENTAJA COMPETITIVA", "COMPETITIVE EDGE", "VANTAGEM COMPETITIVA")}</span>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 border-t border-r border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b border-l border-cyan-400/40 pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ BLOCK #3 — Logistics + Response times ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                <CmsImage slotKey="etracker.machinery" defaultSrc="/e-tracker/machinery.jpg" alt="" className="w-full h-full object-cover aspect-[4/3]" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#06080D]/40 via-transparent to-cyan-500/10 mix-blend-multiply" />
                <div className="absolute bottom-4 left-4 chrome-border rounded-xl px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(6,8,13,0.85)', backdropFilter: 'blur(8px)' }}>
                  <Zap className="w-3.5 h-3.5 text-cyan-300" />
                  <span className="label-mono !text-cyan-300 text-[10px]">{pick("~5S RESPUESTA · 2S EMERGENCIA", "~5S RESPONSE · 2S EMERGENCY", "~5S RESPOSTA · 2S EMERGÊNCIA")}</span>
                </div>
              </div>
              <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-cyan-400/40 pointer-events-none" />
              <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-cyan-400/40 pointer-events-none" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-7"
            >
              <p className="text-white/85 text-base sm:text-lg leading-relaxed">{pick(P5_ES, P5_EN, P5_PT)}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ FEATURES ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: .5, delay: i * 0.06 }}
                className="glass-card chrome-border p-5 group hover:border-cyan-400/50 transition-colors"
                data-testid={`et-feature-${i}`}
              >
                <span className="w-10 h-10 rounded-lg flex items-center justify-center bg-cyan-400/15 border border-cyan-400/40 group-hover:bg-cyan-400/25 transition">
                  <f.Icon className="w-5 h-5 text-cyan-300" />
                </span>
                <h3 className="mt-4 font-tech text-base text-white leading-tight">{f.t}</h3>
                <p className="mt-2 text-[13px] text-white/70 leading-relaxed">{f.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl chrome-border p-8 sm:p-10 md:p-14">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-emerald-500/10 pointer-events-none" />

            {/* Ambient soft glow spot */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(0,240,255,0.10) 0%, transparent 65%)', filter: 'blur(20px)' }}
            />

            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-8 md:gap-10">
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="relative font-display leading-[1.08] tracking-tight max-w-2xl text-[1.6rem] sm:text-3xl md:text-4xl lg:text-[2.6rem] self-center"
                data-testid="etracker-cta-headline"
              >
                <span
                  className="chrome-text italic font-semibold"
                  style={{
                    letterSpacing: '-0.005em',
                    filter: 'drop-shadow(0 2px 12px rgba(0,240,255,0.15)) drop-shadow(0 1px 0 rgba(0,0,0,0.6))',
                  }}
                >
                  <CmsText
                    slotKey="etracker.cta"
                    es="Hablemos del vehículo que necesitas trackear."
                    en="Let's talk about the vehicle you need to track."
                    pt="Vamos falar sobre o veículo que você precisa rastrear."
                    lang={pick("es","en","pt")}
                  />
                </span>
              </motion.h3>
              <a
                {...waProps(content?.contact?.whatsapp ? `https://wa.me/${String(content.contact.whatsapp).replace(/[^0-9]/g,'')}?text=${encodeURIComponent(pick('Hola, me interesa el servicio e-Tracker','Hi, I am interested in the e-Tracker service','Olá, tenho interesse no serviço e-Tracker'))}` : "#contact")}
                className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-tech uppercase tracking-wider text-sm shadow-[0_0_28px_-4px_rgba(0,240,255,0.7)] hover:shadow-[0_0_36px_-4px_rgba(0,240,255,0.95)] transition self-start md:self-auto"
                data-testid="et-cta-whatsapp"
              >
                {waLabel(pick, "Escribir por WhatsApp", "Message on WhatsApp", "Falar pelo WhatsApp")}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer content={content} />
      <FloatingWhatsApp phone={content?.contact?.whatsapp} />
    </div>
  );
}
